import { useEffect, useRef } from 'react';

export const useStickyDiv = ({ headerHeight = 64, buffer = 10 }) => {
  const divRef = useRef(null);
  const originalTopRef = useRef(null);
  const isStickyRef = useRef(false);
  const lastScrollY = useRef(0);

  const mainScroll = document.getElementsByTagName('main')[0];

  useEffect(() => {
    let rafId;

    const handleScroll = () => {
      if (!divRef.current) return;

      // Check if window width is less than 1200px - disable sticky behavior
      if (mainScroll.clientWidth < 1200) {
        // Reset sticky state if currently sticky
        if (isStickyRef.current) {
          divRef.current.style.position = '';
          divRef.current.style.top = '';
          divRef.current.style.width = '';
          divRef.current.style.zIndex = '';
          divRef.current.style.right = '';
          isStickyRef.current = false;
        }
        return;
      }

      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const currentScrollY = mainScroll.scrollTop;
        const rect = divRef.current.getBoundingClientRect();

        // Store original position on first scroll
        if (originalTopRef.current === null) {
          originalTopRef.current = rect.top + currentScrollY;
        }

        // Calculate the threshold more precisely
        const scrollThreshold = originalTopRef.current - headerHeight;
        const shouldBeSticky = currentScrollY >= scrollThreshold;

        if (shouldBeSticky && !isStickyRef.current) {
          // Start sticking - set position fixed and calculate offset
          divRef.current.style.position = 'fixed';
          divRef.current.style.top = `${headerHeight}px`;
          divRef.current.style.width = `${rect.width}px`;
          divRef.current.style.zIndex = '10';
          divRef.current.style.right =
            mainScroll.clientWidth <= 1440 ? '50px' : '56px';
          isStickyRef.current = true;
        } else if (!shouldBeSticky && isStickyRef.current) {
          // Stop sticking - return to normal flow
          divRef.current.style.position = '';
          divRef.current.style.top = '';
          divRef.current.style.width = '';
          divRef.current.style.zIndex = '';
          divRef.current.style.right = '';
          isStickyRef.current = false;
        }

        lastScrollY.current = currentScrollY;
      });
    };

    const handleResize = () => {
      // Reset sticky state on window resize
      if (isStickyRef.current) {
        divRef.current.style.position = '';
        divRef.current.style.top = '';
        divRef.current.style.width = '';
        divRef.current.style.zIndex = '';
        divRef.current.style.right = '';
        isStickyRef.current = false;
      }
      // Reset original position so it recalculates on next scroll
      originalTopRef.current = null;
    };

    if (mainScroll) {
      mainScroll.addEventListener('scroll', handleScroll, { passive: true });
      mainScroll.addEventListener('resize', handleResize, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [headerHeight, buffer, mainScroll]);

  return { divRef, isSticky: isStickyRef.current };
};
